import React, { useEffect, useState } from 'react';
import type { Editor } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import '../pages/BlogProse.css';

type BlockKind = 'paragraph' | 'h2' | 'h3' | 'blockquote';

function activeBlockKind(editor: Editor | null): BlockKind {
  if (!editor) return 'paragraph';
  if (editor.isActive('heading', { level: 2 })) return 'h2';
  if (editor.isActive('heading', { level: 3 })) return 'h3';
  if (editor.isActive('blockquote')) return 'blockquote';
  return 'paragraph';
}

function applyBlock(editor: Editor, kind: BlockKind) {
  const chain = editor.chain().focus();
  switch (kind) {
    case 'paragraph':
      chain.setParagraph().run();
      break;
    case 'h2':
      chain.setHeading({ level: 2 }).run();
      break;
    case 'h3':
      chain.setHeading({ level: 3 }).run();
      break;
    case 'blockquote':
      chain.setBlockquote().run();
      break;
    default:
      break;
  }
}

export interface BlogRichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

const BlogRichTextEditor: React.FC<BlogRichTextEditorProps> = ({ value, onChange, placeholder }) => {
  const [, setToolbarTick] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank', class: 'text-[#0B4778] underline' },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Write your post…',
        emptyEditorClass: 'is-editor-empty',
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        spellCheck: 'true',
        class:
          'blog-prose focus:outline-none min-h-[320px] px-4 py-4 text-sm text-gray-800 leading-relaxed max-w-none',
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
      setToolbarTick((n) => n + 1);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const onSel = () => setToolbarTick((n) => n + 1);
    editor.on('selectionUpdate', onSel);
    return () => {
      editor.off('selectionUpdate', onSel);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const cur = editor.getHTML();
    if (cur === value) return;
    editor.commands.setContent(value, false);
  }, [editor, value]);

  const blockValue = activeBlockKind(editor);

  return (
    <div className="blog-rich-editor">
      <div
        className="flex flex-wrap items-center gap-1 p-2 rounded-t-xl border border-b-0 bg-[#f8fbff]"
        style={{ borderColor: '#dbe3ef' }}
      >
        <span className="tool-bar-select-wrap">
          <select
            className="tool-bar-select"
            value={blockValue}
            onChange={(e) => {
              if (!editor) return;
              applyBlock(editor, e.target.value as BlockKind);
            }}
            aria-label="Text style"
          >
            <option value="paragraph">Paragraph</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="blockquote">Quote</option>
          </select>
          <span className="tool-bar-select-chevron" aria-hidden="true">
            <i className="bi bi-chevron-down" />
          </span>
        </span>

        <button
          type="button"
          className="tool-bar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleBold().run()}
          disabled={!editor}
          title="Bold"
          aria-pressed={editor?.isActive('bold')}
          style={editor?.isActive('bold') ? { background: '#eef2ff', color: '#0B4778' } : undefined}
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className="tool-bar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          disabled={!editor}
          title="Italic"
          aria-pressed={editor?.isActive('italic')}
          style={editor?.isActive('italic') ? { background: '#eef2ff', color: '#0B4778' } : undefined}
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className="tool-bar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
          disabled={!editor}
          title="Underline"
          aria-pressed={editor?.isActive('underline')}
          style={editor?.isActive('underline') ? { background: '#eef2ff', color: '#0B4778' } : undefined}
        >
          <span className="underline">U</span>
        </button>
        <button
          type="button"
          className="tool-bar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleStrike().run()}
          disabled={!editor}
          title="Strikethrough"
          aria-pressed={editor?.isActive('strike')}
          style={editor?.isActive('strike') ? { background: '#eef2ff', color: '#0B4778' } : undefined}
        >
          <s>S</s>
        </button>

        <button
          type="button"
          className="tool-bar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          disabled={!editor}
          title="Bullet list"
          aria-pressed={editor?.isActive('bulletList')}
          style={editor?.isActive('bulletList') ? { background: '#eef2ff', color: '#0B4778' } : undefined}
        >
          <i className="bi bi-list-ul" />
        </button>
        <button
          type="button"
          className="tool-bar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          disabled={!editor}
          title="Numbered list"
          aria-pressed={editor?.isActive('orderedList')}
          style={editor?.isActive('orderedList') ? { background: '#eef2ff', color: '#0B4778' } : undefined}
        >
          <i className="bi bi-list-ol" />
        </button>

        <button
          type="button"
          className="tool-bar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (!editor) return;
            const url = window.prompt('Link URL', editor.getAttributes('link').href || '');
            if (url === null) return;
            const t = url.trim();
            if (t === '') {
              editor.chain().focus().extendMarkRange('link').unsetLink().run();
              return;
            }
            const href = /^https?:\/\//i.test(t) || t.startsWith('mailto:') ? t : `https://${t}`;
            editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
          }}
          disabled={!editor}
          title="Link"
          aria-pressed={editor?.isActive('link')}
          style={editor?.isActive('link') ? { background: '#eef2ff', color: '#0B4778' } : undefined}
        >
          <i className="bi bi-link-45deg" />
        </button>

        <span className="w-px h-5 bg-gray-300 mx-0.5 self-center shrink-0" aria-hidden="true" />

        <button
          type="button"
          className="tool-bar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().undo().run()}
          disabled={!editor || !editor.can().undo()}
          title="Undo"
        >
          <i className="bi bi-arrow-counterclockwise" />
        </button>
        <button
          type="button"
          className="tool-bar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().redo().run()}
          disabled={!editor || !editor.can().redo()}
          title="Redo"
        >
          <i className="bi bi-arrow-clockwise" />
        </button>
      </div>

      <div
        className="border rounded-b-xl bg-white focus-within:ring-2 focus-within:ring-[#0B4778]/20 overflow-hidden"
        style={{ borderColor: '#dbe3ef' }}
      >
        <EditorContent editor={editor} className="blog-editor-content" />
      </div>

      <style>{`
        .tool-bar-btn {
          border: 1px solid #cbd5e1;
          background: #fff;
          border-radius: 8px;
          min-width: 32px;
          min-height: 32px;
          font-size: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .tool-bar-btn:hover:not(:disabled) { background: #eef2ff; color: #0B4778; }
        .tool-bar-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .tool-bar-select-wrap {
          position: relative;
          display: inline-flex;
          align-items: center;
          min-width: 9.5rem;
          height: 32px;
        }
        .tool-bar-select {
          box-sizing: border-box;
          width: 100%;
          border: 1px solid #cbd5e1;
          background: #fff;
          border-radius: 8px;
          height: 32px;
          min-height: 32px;
          padding: 0 1.75rem 0 0.625rem;
          font-size: 0.875rem;
          line-height: 1.25;
          color: #374151;
          cursor: pointer;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
        }
        .tool-bar-select::-ms-expand { display: none; }
        .tool-bar-select-chevron {
          position: absolute;
          right: 0.5rem;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
          color: #64748b;
          font-size: 0.875rem;
          line-height: 1;
          display: flex;
          align-items: center;
        }
        .tool-bar-select:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(11, 71, 120, 0.2);
        }
        .blog-editor-content .ProseMirror {
          outline: none;
        }
        .blog-prose p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #94a3b8;
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
};

export default BlogRichTextEditor;
